import { createAction } from 'redux-starter-kit';
import { requestStart, requestSucceded, requestFailed } from '../../domain/requests/RequestActions';
import { post, get } from '../../../utility/via-fetch';
import { setInspectionSummaries } from '../../domain/inspection/inspectionSummaryActions';
import { setUserSummaries } from '../../domain/users/userSummaryActions';


/**
 * An action that signifies a search request has started.
 * @param {string} payload.searchValue The value that is being searched.
 * @param {string} payload.requestId The id of the request.
 * @example dispatch(fetchViaSearch({ searchValue: '123', requestId: '999' }))
 */
export const fetchViaSearch = createAction('FETCH_VIA_SEARCH');

/**
 * An action that signifies a search request has finished.
 * @param {string} payload.searchValue The value that was being searched.
 * @param {string} payload.requestTime The finish date time. In ISO string format. (e.g. new Date().toISOString())
 * @param {object} payload.inspectionResults The inspection results.
 * @param {boolean} payload.inspectionResults.allSearchResults True if all all potential results were returned.StaticRange
 * @param {array} payload.inspectionResults.summaries An array of the inspection summary ids.
 * @example dispatch(fetchViaSearchDone({
 *    searchValue: '123',
 *    requestTime: '2019-09-02T07:56:13.459Z',
 *    inspectionResults: {
 *      allSearchResults: true,
 *      summaries: [1, 22],
 *    },
 *  }))
 */
export const fetchViaSearchDone = createAction('FETCH_VIA_SEARCH_DONE');

/**
 * An action for loading search results based on the searchValue.
 * @param {string} searchValue The value to search for.
 */
export function requestViaSearch(searchValue) {
  return async (dispatch) => {
    if (!searchValue) {
      // Nothing to search. Don't bother going to the server.
      dispatch(fetchViaSearch({ searchValue }));
      dispatch(fetchViaSearchDone({
        searchValue,
        requestTime: new Date().toISOString(),
        inspectionResults: null,
      }));
      return;
    }

    const requestId = dispatch(requestStart());
    dispatch(fetchViaSearch({ searchValue, requestId }));
    try {
      const { inspectionSearchResults } = await post('/api/search', undefined, { searchValue });

      // Update the summary data
      dispatch(setInspectionSummaries(inspectionSearchResults.results));

      // Mark the searches as done.
      dispatch(fetchViaSearchDone({
        searchValue,
        requestTime: new Date().toISOString(),
        inspectionResults: {
          allSearchResults: inspectionSearchResults.allSearchResults,
          summaries: inspectionSearchResults.results ? inspectionSearchResults.results.map(s => s.inspectionCode) : [],
        },
      }));
      dispatch(requestSucceded(requestId, searchValue));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action that signifies a search request has started.
 * @param {string} payload.searchValue The value that is being searched.
 * @param {string} payload.requestId The id of the request.
 * @example dispatch(fetchUserSearch({ searchValue: '123', requestId: '999' }))
 */
export const fetchUserSearch = createAction('FETCH_USER_SEARCH');

/**
 * An action that signifies a search request has finished.
 * @param {string} payload.searchValue The value that was being searched.
 * @param {string} payload.requestTime The finish date time. In ISO string format. (e.g. new Date().toISOString())
 * @param {object} payload.searchResults The search results.
 * @param {boolean} payload.searchResults.allSearchResults True if all all potential results were returned.
 * @param {array} payload.searchResults.summaries An array of the user summaries.
 * @example dispatch(fetchUserSearchDone({
 *    searchValue: '123',
 *    requestTime: '2019-09-02T07:56:13.459Z',
 *    searchResults: {
 *      allSearchResults: true,
 *      summaries: [{ userId: 1, username: 'stsf', companyName: '...', ...}, ...],
 *    },
 *  }))
 */
export const fetchUserSearchDone = createAction('FETCH_USER_SEARCH_DONE');

/**
 * An action for loading search results based on the searchValue.
 * @param {string} searchValue The value to search for.
 */
export function requestUserSearch(searchValue) {
  return async (dispatch) => {
    if (!searchValue || searchValue.length < 3) {
      // Not enough to search. Don't bother going to the server.
      dispatch(fetchUserSearch({ searchValue }));
      dispatch(fetchUserSearchDone({
        searchValue,
        requestTime: new Date().toISOString(),
        searchResults: null,
      }));
      return;
    }

    const requestId = dispatch(requestStart());
    dispatch(fetchUserSearch({ searchValue, requestId }));
    try {
      const userSearchResults = await get('/api/users/search', { search: searchValue }, { });
      // Mark the searches as done.
      dispatch(fetchUserSearchDone({
        searchValue,
        requestTime: new Date().toISOString(),
        searchResults: {
          allSearchResults: userSearchResults.allSearchResults,
          summaries: userSearchResults.results ? userSearchResults.results : [],
        },
      }));
      dispatch(setUserSummaries(userSearchResults.results ? userSearchResults.results : []));
      dispatch(requestSucceded(requestId, searchValue));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}
